import React from 'react';
import { useParams } from 'react-router-dom';
import { roles, routes } from '../../../../../constants';
import { alertOptions } from '../../../../../constants/amr-pipeline/alertOptions';
import { useAppSelector } from '../../../../../effects/useAppSelector';
import { StatisticsAlertOption } from '../../../../../types/amr-pipeline/enums/AlertOption';
import { AlertOption } from '../../../../../types/email-preferences/EmailPreferences';
import { arrayUtils, isRequesting } from '../../../../../utils';
import { TabItem } from '../../../../bidding/common/tab-list';
import { EmailPreferencesTab } from '../EmailPreferencesContent';
import { user } from '../../../../../user';
import { usePageConfig } from '../../../../common/pageConfig/usePageConfig';
import { PageConfigType } from '../../../../../types/page-config/PageConfigType';
import { SubscriptionFeature } from '../../../../../types/billing/SubscriptionFeature';
import { emailPreferencesUtils } from '../../../../../utils/email-preferences.utils';
import { PortfolioUserConfigFilter, UserConfigFilter } from '../../../../../types/user-config/UserConfigFilter';
import { hasBankAnalyticsAlertsAccess, hasBankAnalyticsAlertsChangeAccess } from '../../../../../utils/amr-pipeline.utils';
import { CompanyAlertsValue, SecondaryStatsAlertsValue } from '../../../../../types/user-config/UserConfig';

interface Props {
    tabPath: EmailPreferencesTab,
    title: React.ReactNode;
    disabled?: boolean;
}

type AlertOptionType = Partial<{ alertOption: AlertOption }>;

const getDefaultGlobalAlertStatus = (alerts: AlertOptionType[] = []) => {
    let status = null;
    for (let option of alertOptions) {
        if (alerts.every((f) => f.alertOption === option)) {
            status = option;
            break;
        }
    }
    return status;
};

const getGlobalStatusCount = (alerts?: AlertOptionType[]) =>
    Number(
        Boolean(alerts) &&
            getDefaultGlobalAlertStatus(alerts) !== AlertOption.Never
    );

const getSingleStatusCount = (
    alert?: AlertOption | StatisticsAlertOption
) => Number(Boolean(alert) && alert !== AlertOption.Never);

export function EmailPreferencesTabItem({ tabPath, title, disabled }: Props) {
    const { tab } = useParams<{ tab: string }>();

    const isSellerCollateralManager = user.hasRoles(roles.CollateralManager);
    const withImApAccess = user.hasFeatures(SubscriptionFeature.IssuanceMonitorFullAccess);
    const withManagerProfileAccess = user.hasFeatures(SubscriptionFeature.ManagerProfileManagerRelatedAlerts);
    const withBwicAccess = user.hasFeatures(SubscriptionFeature.BwicMonitorAlerts);
    const withDiAccess = user.hasFeatures(SubscriptionFeature.InventoryAlerts);
    const withDealerProfileAccess = user.hasFeatures(SubscriptionFeature.DealerProfileEmailAlerts);
    const portfolioImAlertsAccess = user.hasAllFeatures(...[
        SubscriptionFeature.IssuanceMonitorFullAccess,
        SubscriptionFeature.PortfolioAlerts,
    ]);

    const userCompany = useAppSelector(s => s.issuanceMonitor.amrPipelineCommon.userCompany);
    const banks = useAppSelector(s => s.manageEmailPreferences.banks);
    const requestStateGetPreferences = useAppSelector(s => s.manageEmailPreferences.requestStateGetPreferences);
    const editPreferences = useAppSelector(s => s.manageEmailPreferences.editPreferences);
    const editIssuanceMonitorPreferences = useAppSelector(s => s.manageEmailPreferences.editIssuanceMonitorPreferences);
    const editCloManagersPreferences = useAppSelector(s => s.manageEmailPreferences.editCloManagersPreferences);
    const editSavedFiltersPreferences = useAppSelector(s => s.manageEmailPreferences.editSavedFiltersPreferences);
    const editBanksPreferences = useAppSelector(s => s.manageEmailPreferences.editBanksPreferences);
    const editArrangerPipelinePreferences = useAppSelector(s => s.manageEmailPreferences.editArrangerPipelinePreferences);
    const portfolioSavedFilterAlerts = useAppSelector(s => s.manageEmailPreferences.portfolioSavedFilterAlerts);
    const { config: bwicMonitorPageConfig } = usePageConfig(PageConfigType.BwicMonitorAll, true);
    const { config: portfolioPageConfig } = usePageConfig(PageConfigType.Portfolio, true);
    const { config: dealerInventoryPageConfig } = usePageConfig(PageConfigType.DealerInventory, true);

    const withAnalyticsAccess = hasBankAnalyticsAlertsAccess(banks, userCompany);
    const withAnalyticsChangeAccess = hasBankAnalyticsAlertsChangeAccess(banks, userCompany);

    const getLimitedCloStatusCount = (
        alerts: (SecondaryStatsAlertsValue | CompanyAlertsValue)[] = [],
        withAccess = false
    ) => {
        if (withAccess) {
            return getGlobalStatusCount(alerts);
        }

        if (isSellerCollateralManager) {
            return getGlobalStatusCount(
                alerts.filter(
                    (a) => a.companyReferenceName === userCompany?.referenceName
                )
            );
        }

        return 0;
    };

    const getCloManagerCounters = () => {
        const cloManagerAnalyticsValue =
            editCloManagersPreferences?.cloManagerAnalyticsAlert.value.find(
                (a) => a.companyReferenceName === userCompany?.referenceName
            );

        return [
            ...(isSellerCollateralManager
                ? [getSingleStatusCount(cloManagerAnalyticsValue?.alertOption)]
                : []),
            getLimitedCloStatusCount(
                editCloManagersPreferences?.cloManagerIssuanceMonitorAlert
                    .value,
                withManagerProfileAccess && withImApAccess
            ),
            getLimitedCloStatusCount(
                editCloManagersPreferences?.cloManagerArrangerPipelineAlert
                    .value,
                withManagerProfileAccess && withImApAccess
            ),
            getLimitedCloStatusCount(
                editCloManagersPreferences?.cloManagerBwicAlert.value,
                withBwicAccess
            ),
            getLimitedCloStatusCount(
                editCloManagersPreferences?.cloManagerDealerInventoryAlert
                    .value,
                withDiAccess
            ),
            getGlobalStatusCount(
                editCloManagersPreferences?.cloManagerOutOfNcAlert.value
            ),
            getGlobalStatusCount(
                editCloManagersPreferences?.cloManagerOutOfRiAlert.value
            ),
        ];
    };

    const getBanksCounters = () => {
        const bankDiSectionCounter = getGlobalStatusCount(editBanksPreferences?.bankDealerInventoryAlert.value);
        const bankImSectionCounter = getGlobalStatusCount(editBanksPreferences?.bankIssuanceMonitorAlert.value);
        const bankApSectionCounter = getGlobalStatusCount(editBanksPreferences?.bankArrangerPipelineAlert.value);
        const bankOutOfNcSectionCounter = getGlobalStatusCount(editBanksPreferences?.bankOutOfNcAlert.value);
        const bankOutOfRiSectionCounter = getGlobalStatusCount(editBanksPreferences?.bankOutOfRiAlert.value);

        const bankCounter = [
            withDiAccess ? bankDiSectionCounter : 0,
            ...(withDealerProfileAccess && withImApAccess ? [bankImSectionCounter, bankApSectionCounter] : [0, 0]),
            bankOutOfNcSectionCounter,
            bankOutOfRiSectionCounter,
        ];

        if (withAnalyticsAccess) {
            const bankAnalyticsCounter = withAnalyticsChangeAccess
                ? getSingleStatusCount(
                    editBanksPreferences?.bankAnalyticsAlert.value?.find(
                        a => a.companyReferenceName === userCompany?.referenceName,
                    )?.alertOption
                  )
                : 0;

            bankCounter.unshift(bankAnalyticsCounter);
        }

        return bankCounter;
    }

    const getBwicMonitorCounters = () => {
        const isSavedFiltersAlertOptionActive =
            bwicMonitorPageConfig?.filters.some(f => {
                const original = f.alertOption;
                const changed = editSavedFiltersPreferences[f.referenceName];
                const current = changed ?? original;

                return current !== AlertOption.Never
            });

        const canUseBwicMonitorAlerts = user.hasFeatures(SubscriptionFeature.BwicMonitorAlerts);

        return [
            canUseBwicMonitorAlerts ? Number(editPreferences.bwicByRatingAlertState !== AlertOption.Never) : 0,
            canUseBwicMonitorAlerts ? Number(isSavedFiltersAlertOptionActive) : 0,
            canUseBwicMonitorAlerts ? Number(editPreferences.secondaryWeeklySummaryAlertState === AlertOption.Weekly) : 0
        ];
    }

    const getInventoryCounters = () => {
        const isSavedFiltersAlertOptionActive =
            dealerInventoryPageConfig?.filters.some(f => {
                const original = f.alertOption;
                const changed = editSavedFiltersPreferences[f.referenceName];
                const current = changed ?? original;

                return current !== AlertOption.Never
            });

        const canUseInventoryAlerts = user.hasFeatures(SubscriptionFeature.InventoryAlerts);

        return [
            canUseInventoryAlerts ? Number(editPreferences.dealerInventoryAlertState !== AlertOption.Never) : 0,
            canUseInventoryAlerts ? Number(isSavedFiltersAlertOptionActive) : 0
        ];
    }

    const getPortfolioCounters = () => {
        const getActualFilter = (filter: UserConfigFilter) => {
            const original = (filter as PortfolioUserConfigFilter);
            const changed = portfolioSavedFilterAlerts[filter.referenceName];
            return changed ?? { bwicAlert: original.bwicAlertOption, inventoryAlert: original.dealerInventoryAlertOption };
        }

        const isSavedFiltersBwicAlertOptionActive = portfolioPageConfig?.filters.some(f =>
            getActualFilter(f).bwicAlert === AlertOption.Instant
        );
        const isSavedFiltersInventoryAlertOptionActive = portfolioPageConfig?.filters.some(f =>
            getActualFilter(f).inventoryAlert === AlertOption.Instant
        );

        return [
            Number(emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioBwicAlertState) !== AlertOption.Never),
            Number(emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioDealersInventoryAlertState) !== AlertOption.Never),
            Number(portfolioImAlertsAccess && emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioCleansingNoticeAlertState) !== AlertOption.Never),
            Number(portfolioImAlertsAccess && emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioIssuanceMonitorAlertState) !== AlertOption.Never),
            Number(portfolioImAlertsAccess && emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioOutOfRiPeriodAlertState) !== AlertOption.Never),
            Number(portfolioImAlertsAccess && emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioOutOfNcPeriodAlertState) !== AlertOption.Never),
            Number(portfolioImAlertsAccess && emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.portfolioRollerDeadlineAlertState) !== AlertOption.Never),
            user.hasFeatures(SubscriptionFeature.PortfolioAlerts) ? Number(isSavedFiltersBwicAlertOptionActive) : 0,
            user.hasFeatures(SubscriptionFeature.PortfolioAlerts) ? Number(isSavedFiltersInventoryAlertOptionActive) : 0
        ];
    }

    const getCounters = () => {
        switch (tabPath) {
            case EmailPreferencesTab.Dashboard:
                return [user.hasFeatures(SubscriptionFeature.CanUseDashboardAlerts)
                    ? Number(editPreferences.dailyDashboardEmail !== AlertOption.Never)
                    : 0
                ];
            case EmailPreferencesTab.BwicMonitor:
                return getBwicMonitorCounters();
            case EmailPreferencesTab.MyBwics:
                return [
                    Number(emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.newBwicAlertState) !== AlertOption.Never),
                    Number(emailPreferencesUtils.convertAlertEnabledStateToAlertOption(editPreferences.bwicReminderAlertState) !== AlertOption.Never)
                ]
            case EmailPreferencesTab.IssuanceMonitor:
                const alertsCount = [
                    getSingleStatusCount(editIssuanceMonitorPreferences?.imAlertConfig.value.alertOption),
                    getSingleStatusCount(editIssuanceMonitorPreferences?.newTransactionAlertConfig.value.alertOption),
                    getSingleStatusCount(editIssuanceMonitorPreferences?.weeklyStatsAlertConfig.value.emailOption),
                    getGlobalStatusCount(editIssuanceMonitorPreferences?.filtersConfig.ImFilter.value),
                ];

                return withImApAccess ? alertsCount : alertsCount.map(i => 0);
            case EmailPreferencesTab.ArrangerPipeline:
                return [
                    withImApAccess
                        ? getSingleStatusCount(editArrangerPipelinePreferences?.apAlertConfig.value.alertOption)
                        : 0
                ];
            case EmailPreferencesTab.Portfolio: return getPortfolioCounters();
            case EmailPreferencesTab.DealerInventory: return getInventoryCounters();
            case EmailPreferencesTab.CloManagers:
                return getCloManagerCounters();
            case EmailPreferencesTab.Banks:
                return getBanksCounters();
            default:
                return [];
        }
    }

    const counters = getCounters();
    const count = arrayUtils.sum(counters, (item) => item);

    return (
        <TabItem
            active={tabPath === tab}
            title={<>{title} <span className="text-regular text-warm-grey">({count}/{counters.length})</span></>}
            path={routes.profileNotificationsTab(tabPath)}
            disabled={isRequesting(requestStateGetPreferences) || disabled}
        />
    )
}
